import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  SidePanelLoader,
  Popup,
  ResponseError,
  getResponseErrors,
  responseErrorsResolver,
  ButtonPrimary,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetEmployeesCurrencyQuery,
  useToggleEmployeesCurrenciesActiveMutation,
  useToggleBaseCurrencyMutation,
  useToggleDefaultCreationMutation,
  useUpdateCurrencyMutation,
} from '../store/services/currencies';
import Form from '../../../components/settings/currencies/form';
import { CurrencyItem } from '../types';

import ROUTES from '../../../routes';

const CurrencyForm = () => {
  const {
    control,
    reset,
    formState: { errors },
    setValue,
    handleSubmit,
    setError,
  } = useForm<CurrencyItem>({
    defaultValues: {
      active: false,
    },
  });

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [isBaseCurrency, setIsBaseCurrency] = useState(false);
  const [shouldCreateBalanceForClients, setShouldCreateBalanceForClients] =
    useState(false);
  const [
    shouldCreateBalanceForClientsError,
    setShouldCreateBalanceForClientsError,
  ] = useState('');
  const [errorList, setErrorList] = useState('');

  const { formValues, baseCurrency } = useAppSelector(
    (state) => state.settings.currencies,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const navigate = useNavigate();

  const { currencyId } = useParams();
  const { search } = useLocation();

  const [getEmployeesCurrency, { isLoading, isFetching }] =
    useLazyGetEmployeesCurrencyQuery();
  const [toggleCurrencyStatus, { isSuccess, isLoading: toggeInProgress }] =
    useToggleEmployeesCurrenciesActiveMutation();
  const [
    toggleCurrencyDefaultCreation,
    { isLoading: defaultCreationInProgress, isError: defaultCreationIsError },
  ] = useToggleDefaultCreationMutation();
  const [toggleBaseCurrency] = useToggleBaseCurrencyMutation();
  const [updateCurrency, { isLoading: requestInProgress }] =
    useUpdateCurrencyMutation();

  const toggleEmployeesCurrenciesActive = async (active: boolean) => {
    await toggleCurrencyStatus({
      active,
      id: currencyId as string,
    });
  };

  const toggleBaseCurrenyAction = async () => {
    await toggleBaseCurrency(currencyId as string);
  };

  const toggleDefaultCreation = async (checked: boolean) => {
    setShouldCreateBalanceForClients(checked);

    const response = (await toggleCurrencyDefaultCreation({
      id: currencyId as string,
      body: {
        shouldCreateBalanceForClients: checked,
      },
    })) as { error: ResponseError | { data: { message: string } } };

    if (response.error) {
      if (
        Array.isArray(response.error.data.message) &&
        response?.error?.data?.message[0]
      ) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setShouldCreateBalanceForClientsError(
            response?.error?.data?.message[0]?.value as string,
          );
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setShouldCreateBalanceForClientsError(response?.error?.data?.message);
      } else {
        setShouldCreateBalanceForClientsError(
          'Internal Server Error. Try Again',
        );
      }
    }
  };

  const getEmployeesCurrencyAction = useCallback(async () => {
    if (currencyId) {
      const response = (await getEmployeesCurrency(currencyId)) as {
        error?: ResponseError;
      };

      if (response?.error) {
        getResponseErrors({
          error: response?.error,
          setErrorList,
        });
      }
    }
  }, [currencyId]);

  const onSubmit = handleSubmit(async (values) => {
    const response = (await updateCurrency({
      id: currencyId as string,
      body: {
        decimals: Number(values.decimals),
      },
    })) as { error?: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError: setErrorList,
      });
    }
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setPopupVisible(false);
    setErrorList('');

    setTimeout(() => {
      navigate(`${ROUTES.CURRENCIES}${search || ''}`);
    }, 100);
  }, [search]);

  useEffect(() => {
    if (defaultCreationIsError) {
      setShouldCreateBalanceForClients(!shouldCreateBalanceForClients);
    }
  }, [defaultCreationIsError]);

  useEffect(() => {
    if (isSuccess) {
      setPopupVisible(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (baseCurrency && currencyId && baseCurrency.id === currencyId) {
      setIsBaseCurrency(true);
    }
  }, [baseCurrency, currencyId]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (currencyId) {
      getEmployeesCurrencyAction();
    }
  }, [getEmployeesCurrencyAction, currencyId]);

  useEffect(() => {
    if (formValues && currencyId) {
      reset(formValues);

      setShouldCreateBalanceForClients(
        formValues.shouldCreateBalanceForClients as boolean,
      );
    }
  }, [reset, formValues, currencyId]);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <Form
        control={control}
        errors={errors}
        currencyId={currencyId}
        setPopupVisible={setPopupVisible}
        toggleActive={toggleEmployeesCurrenciesActive}
        isBaseCurrency={isBaseCurrency}
        setIsBaseCurrency={setIsBaseCurrency}
        toggleBaseCurrenyAction={toggleBaseCurrenyAction}
        shouldCreateBalanceForClients={shouldCreateBalanceForClients}
        toggleDefaultCreation={toggleDefaultCreation}
        shouldCreateBalanceForClientsError={shouldCreateBalanceForClientsError}
        defaultCreationInProgress={defaultCreationInProgress}
        isEditable={activePermissions?.includes(
          PermissionEnum.SETTINGS_CURRENCIES_EDIT_ACTION,
        )}
      />
    );
  }

  return (
    <>
      <Popup
        title="You disable off the currency"
        description="The currency will not be available for exchange and for transactions. Disable?"
        onCancelClick={() => {
          setPopupVisible(false);
          setValue('active', true);
        }}
        onOkClick={() => toggleEmployeesCurrenciesActive(false)}
        cancelButtonText="Cancel"
        okButtonText={toggeInProgress ? 'Loading....' : 'Yes, disable'}
        isVisible={popupVisible}
        parent={document.getElementById('currency-popup') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.Fees}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        sidepanelExpandedKey="currency-details"
        errorSidepanel={errorList}
        title={
          isLoading || isFetching
            ? 'Currency'
            : (formValues?.description as string)
        }
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_CURRENCIES_EDIT_ACTION,
            ) && (
              <ButtonPrimary
                text={requestInProgress ? 'Loading...' : 'Save'}
                onClick={onSubmit}
                disabled={requestInProgress}
                bold
              />
            )}

            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </>
        }
      >
        {content}
      </SidePanel>
    </>
  );
};

export default CurrencyForm;
